import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { Loader } from '../common/Loader';
import { useRequests } from '../../hooks/useRequests';

type TabType = 'paper' | 'certificate';

export const RequestsList: React.FC = () => {
  const [tab, setTab] = useState<TabType>('paper');
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Поля для справки
  const [inn, setInn] = useState('');
  const [year, setYear] = useState('');
  const [city, setCity] = useState('');

  // Поля для сертификата
  const [fio, setFio] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');

  const {
    paperRequests,
    certRequests,
    isLoading,
    error,
    handleCreatePaperRequest,
    handleCreateCertificateRequest,
  } = useRequests();

  const resetForm = () => {
    setInn('');
    setYear('');
    setCity('');
    setFio('');
    setPhone('');
    setAmount('');
    setSubmitError(null);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      let result;
      if (tab === 'paper') {
        if (!inn.trim() || !year.trim() || !city.trim()) {
          setSubmitError('Заполните все поля');
          setSubmitting(false);
          return;
        }
        const comment = `ИНН: ${inn}\nГод: ${year}\nГород: ${city}`;
        result = await handleCreatePaperRequest(comment);
      } else {
        if (!fio.trim() || !phone.trim() || !amount.trim()) {
          setSubmitError('Заполните все поля');
          setSubmitting(false);
          return;
        }
        const comment = `ФИО: ${fio}\nТелефон: ${phone}\nСумма: ${amount}`;
        result = await handleCreateCertificateRequest(comment);
      }
      if (result.success) {
        setShowDialog(false);
        resetForm();
      } else {
        setSubmitError((result as any).error || 'Ошибка при создании заявки');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loader message="Загрузка заявок..." />;
  }

  if (error) {
    return (
      <Paper sx={{ padding: 2, backgroundColor: '#ffebee' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  const currentRequests = tab === 'paper' ? paperRequests : certRequests;

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ marginBottom: 2 }}>
        <Tab label="Справки" value="paper" />
        <Tab label="Сертификаты" value="certificate" />
      </Tabs>

      <Box sx={{ marginBottom: 2 }}>
        <Button variant="contained" onClick={() => setShowDialog(true)}>
          + Заказать {tab === 'paper' ? 'справку' : 'сертификат'}
        </Button>
      </Box>

      {currentRequests.length === 0 ? (
        <Paper sx={{ padding: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Нет {tab === 'paper' ? 'заявок на справки' : 'заявок на сертификаты'}
          </Typography>
        </Paper>
      ) : (
        <List>
          {currentRequests.map((req) => (
            <ListItem
              key={req.id}
              component={Paper}
              sx={{ marginBottom: 1, padding: 2, display: 'flex', justifyContent: 'space-between' }}
            >
              <Box>
                {(req as any).comment && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {(req as any).comment}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  {new Date(req.createdAt).toLocaleDateString('ru-RU')}
                </Typography>
              </Box>
              <Chip
                label={req.processed ? 'Выполнена' : 'В обработке'}
                color={req.processed ? 'success' : 'warning'}
                size="small"
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* Диалог создания */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Заказать {tab === 'paper' ? 'справку' : 'сертификат'}
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ marginBottom: 2, marginTop: 1 }}>
              {submitError}
            </Alert>
          )}

          {tab === 'paper' ? (
            // Поля для справки
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                Пожалуйста, введите данные для оформления справки:
              </Typography>
              <TextField
                fullWidth
                label="ИНН"
                value={inn}
                onChange={(e) => setInn(e.target.value)}
                sx={{ marginBottom: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Год"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                sx={{ marginBottom: 2 }}
                placeholder="За какой год необходима справка"
                required
              />
              <TextField
                fullWidth
                label="Город"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Город, в котором проходили исследование"
                required
              />
            </>
          ) : (
            // Поля для сертификата
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                Пожалуйста, введите данные для оформления сертификата:
              </Typography>
              <TextField
                fullWidth
                label="ФИО"
                value={fio}
                onChange={(e) => setFio(e.target.value)}
                sx={{ marginBottom: 2 }}
                placeholder="ФИО пациента, которому подарите сертификат"
                required
              />
              <TextField
                fullWidth
                label="Телефон пациента"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                sx={{ marginBottom: 2 }}
                placeholder="Телефон пациента, которому вы хотите подарить сертификат"
                required
              />
              <TextField
                fullWidth
                label="Сумма сертификата"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Введите сумму сертификата"
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)} disabled={submitting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting}
          >
            {submitting ? 'Отправка...' : 'Заказать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
